import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

export default function HeroSection({
  title,
  highlightedWord,
  subtitle,
  primaryCTA,
  secondaryCTA,
  backgroundImage,
  showScrollIndicator = true,
  minHeight = 'min-h-screen'
}) {
  return (
    <section 
      className={`relative ${minHeight} flex items-center justify-center overflow-hidden`}
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
        {/* Gold Ornament */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="gold-divider mb-8"
        >
          <div className="diamond" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-display font-light text-white leading-tight mb-6"
        >
          {title.split(highlightedWord).map((part, i) => (
            <span key={i}>
              {part}
              {i === 0 && highlightedWord && (
                <span className="text-[#d1af6a]">{highlightedWord}</span>
              )}
            </span>
          ))}
        </motion.h1>

        {/* Subtitle */}
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg sm:text-xl text-[#c8c6c4] max-w-2xl mx-auto mb-10 font-body"
          >
            {subtitle}
          </motion.p>
        )}

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          {primaryCTA && (
            <Link
              to={primaryCTA.href}
              className="btn-primary"
              data-testid="hero-primary-cta"
            >
              {primaryCTA.label}
            </Link>
          )}
          {secondaryCTA && (
            <Link
              to={secondaryCTA.href}
              className="btn-secondary"
              data-testid="hero-secondary-cta"
            >
              {secondaryCTA.label}
            </Link>
          )}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-scroll-bounce"
        >
          <ChevronDown className="w-8 h-8 text-[#d1af6a]" />
        </motion.div>
      )}
    </section>
  );
}
